import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Elements, EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { CreditCard, ShieldCheck, AlertCircle, ArrowLeft } from "lucide-react";
import { formatCurrency } from "@tellescope/utils";
import { Separator, cn } from "@tellescope/ui";
import { Button } from "@tellescope/design-system";
import type { EnduserSession } from "@tellescope/sdk";
import { useCartStore } from "../cartStore";
import { UnconfiguredShopProductError, usePrepareShopCheckout } from "../usePrepareShopCheckout";
import { useLoadStripeInstance } from "../useLoadStripeInstance";
import { formatShopPrice } from "../formatShopPrice";
import { groupSubscriptionTotals, formatCadenceTotal } from "../groupSubscriptionTotals";
import { ShopPaymentElementForm } from "../molecules/ShopPaymentElementForm";
import type { PaymentCartRow } from "../useShopBlockLogic";

interface PaymentSummaryProps {
  cartRows: PaymentCartRow[];
  onBack: () => void;
  /** Label for the back button — reflects whichever step onBack actually returns to (cart, or intake questions). */
  backLabel: string;
  onConfirm: () => void;
  /**
   * Navigates to the catalog after a successful order (the confirmation screen's "Back to Shop"
   * button) — deliberately separate from onBack, which returns to cart/questions and would land on
   * the now-emptied cart (clearCart runs on confirmation) instead of the catalog.
   */
  onBackToShop: () => void;
  /**
   * Admin-authored confirmation-screen message; falls back to a generic default when unset. Kept
   * deliberately vague by default (no shipping/tracking/email claims) since fulfillment specifics
   * vary per org.
   */
  confirmationMessage: string | undefined;
  /** The shop block's configured Stripe Custom account, if any — passed through to checkout unchanged. */
  stripeKey: string | undefined;
  /** The current enduser's session, threaded down for usePrepareShopCheckout's Tellescope API calls — this package doesn't own the app's session state. */
  session: EnduserSession | null;
}

export const PaymentSummary = ({
  cartRows,
  onBack,
  backLabel,
  onConfirm,
  onBackToShop,
  confirmationMessage,
  stripeKey,
  session,
}: PaymentSummaryProps) => {
  const { t } = useTranslation("shop");
  const [eligibilityExpanded, setEligibilityExpanded] = useState(false);
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const clearCart = useCartStore((state) => state.clearCart);
  const resetCheckoutProgress = useCartStore((state) => state.resetCheckoutProgress);
  const {
    resolveAndPrepareCheckout,
    data: checkoutData,
    error: checkoutError,
    isPending: isPreparingCheckout,
  } = usePrepareShopCheckout(session);

  const eligibleRows = useMemo(() => cartRows.filter((row) => !row.ineligible), [cartRows]);
  const currency = cartRows[0]?.variant.currency ?? "usd";
  // CartPanel's "Checkout" button is the only entry point into this component (see ShopBlock.tsx)
  // and is disabled whenever the cart holds more than one distinct subscription billing cadence —
  // Stripe Checkout itself can only charge one billing interval per session. So eligibleRows is
  // assumed to contain at most one cadence by the time it's reached here; cadenceTotal is that one
  // subscription subtotal (or undefined if the cart is entirely one-time items).
  const cadenceTotal = useMemo(() => groupSubscriptionTotals(t, eligibleRows)[0], [t, eligibleRows]);
  const oneTimeTotal = useMemo(() => {
    let sum = 0;
    for (const row of eligibleRows) {
      if (!row.variant.isSubscription) sum += row.variant.amount * row.quantity;
    }
    return sum;
  }, [eligibleRows]);

  const orderTotalLabel =
    [
      cadenceTotal ? formatCadenceTotal(cadenceTotal) : null,
      oneTimeTotal > 0 ? formatCurrency(oneTimeTotal, currency, { fromSmallestUnit: true }) : null,
    ]
      .filter(Boolean)
      .join(" + ") || formatCurrency(0, currency, { fromSmallestUnit: true });

  const stripeQuery = useLoadStripeInstance(checkoutData?.publishableKey, checkoutData?.stripeAccount);

  const handlePlaceOrder = () => {
    if (isPreparingCheckout) return;
    setCheckoutStarted(true);
    resolveAndPrepareCheckout(
      eligibleRows.map((row) => ({ stripePriceId: row.variant.stripePriceId, quantity: row.quantity })),
      stripeKey
    ).catch(() => {
      // checkoutError below already surfaces the failure; nothing further to do here.
    });
  };

  // Once checkoutStarted is true, the embedded Checkout / Payment Element UI below takes over the
  // whole screen with no back button of its own — without this, a hard failure inside either (e.g.
  // Stripe's embedded session itself erroring, not just a declined card) stranded the enduser with
  // no way back to the order summary to retry or edit their cart.
  const handleCancelCheckout = () => setCheckoutStarted(false);

  const handleCheckoutComplete = () => {
    resetCheckoutProgress();
    clearCart();
    setConfirmed(true);
    onConfirm();
  };

  if (confirmed) {
    return (
      <div className="p-8 max-w-md mx-auto w-full flex flex-col items-center justify-center min-h-[60vh] text-center gap-5">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
          <ShieldCheck className="w-10 h-10 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">{t("Order Confirmed")}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {confirmationMessage || t("Your order has been placed.")}
          </p>
        </div>
        <Button onClick={onBackToShop} variant="outline">
          {t("Back to Shop")}
        </Button>
      </div>
    );
  }

  // Two distinct Stripe integrations share this response shape, disambiguated by isCheckout:
  // Custom (isCheckout: true) returns an embedded Checkout Session clientSecret (cs_...), rendered
  // via EmbeddedCheckout with no stripeAccount option. Connect (isCheckout absent) returns a
  // PaymentIntent clientSecret (pi_...) instead, which EmbeddedCheckout cannot render — that path
  // needs Elements + a PaymentElement, confirmed explicitly via ShopPaymentElementForm.
  if (checkoutStarted && checkoutData?.isCheckout && stripeQuery.data) {
    return (
      <div className="p-8 max-w-2xl mx-auto w-full" style={{ maxWidth: "680px" }}>
        <Button
          variant="ghost"
          onClick={handleCancelCheckout}
          className="mb-6 gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t("Back to order summary")}
        </Button>
        <EmbeddedCheckoutProvider
          stripe={stripeQuery.data}
          options={{ clientSecret: checkoutData.clientSecret, onComplete: handleCheckoutComplete }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    );
  }

  if (checkoutStarted && checkoutData && !checkoutData.isCheckout && stripeQuery.data) {
    return (
      <div className="p-8 max-w-2xl mx-auto w-full" style={{ maxWidth: "680px" }}>
        <Button
          variant="ghost"
          onClick={handleCancelCheckout}
          className="mb-6 gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t("Back to order summary")}
        </Button>
        <Elements stripe={stripeQuery.data} options={{ clientSecret: checkoutData.clientSecret }}>
          <ShopPaymentElementForm businessName={checkoutData.businessName} onConfirmed={handleCheckoutComplete} />
        </Elements>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl mx-auto w-full" style={{ maxWidth: "680px" }}>
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-6 gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {backLabel}
      </Button>

      <h2 className="text-2xl font-bold text-foreground mb-1">{t("Payment")}</h2>
      <p className="text-sm text-muted-foreground mb-8">{t("Review your order before completing payment.")}</p>

      <div className="flex flex-col gap-8">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">{t("Order Summary")}</h3>
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {cartRows.map((row, i) => (
              <div key={row.variant.stripePriceId}>
                <div className="flex items-start justify-between px-5 py-4">
                  <div>
                    <p
                      className={cn(
                        "text-sm font-medium",
                        row.ineligible ? "text-muted-foreground line-through" : "text-foreground"
                      )}
                    >
                      {row.product.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {row.product.category ? `${row.product.category} · ` : ""}
                      {t("Qty {{quantity}}", { quantity: row.quantity })} ·{" "}
                      {row.variant.isSubscription ? t("Subscription") : t("One-time")}
                    </p>
                    {/*
                      row.ineligible can only be true via the form-gating path (PaymentCartRow is
                      only ever built by snapshotPaymentCartRows in useShopBlockLogic.ts, which marks
                      a row ineligible only when a gated medication's intake status is "failed") — v1
                      ships with no form-gated products, so this branch is structurally unreachable
                      today. Left in place for the forms fast follow's real accept/reject gating.
                    */}
                    {row.ineligible && (
                      <div className="mt-2 flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                        <span className="text-xs font-medium text-amber-700">{t("Not eligible")}</span>
                        <span className="text-muted-foreground text-xs">·</span>
                        <Button
                          variant="link"
                          size="xs"
                          onClick={() => setEligibilityExpanded((v) => !v)}
                          className="h-auto p-0 text-xs font-medium"
                        >
                          {t("More information")}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p
                      className={cn(
                        "text-sm font-semibold",
                        row.ineligible ? "text-muted-foreground line-through" : "text-foreground"
                      )}
                    >
                      {formatShopPrice(t, { ...row.variant, amount: row.variant.amount * row.quantity })}
                    </p>
                    {row.ineligible && <p className="text-xs text-muted-foreground mt-0.5">{t("Not charged")}</p>}
                  </div>
                </div>
                {/* Unreachable in v1 — see the row.ineligible comment above. */}
                {row.ineligible && eligibilityExpanded && (
                  <div className="mx-5 mb-4 -mt-1 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                    <p className="text-xs leading-relaxed text-amber-900">
                      {t(
                        "Based on your intake responses {{title}} isn't appropriate to prescribe at this time. It has been removed from your order and you won't be charged for it. A member of the care team may follow up with alternatives and next steps.",
                        { title: row.product.title }
                      )}
                    </p>
                  </div>
                )}
                {i < cartRows.length - 1 && <Separator />}
              </div>
            ))}
            <Separator />
            <div className="px-5 py-3 space-y-2">
              {cadenceTotal && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("Subscription subtotal")}</span>
                  <span className="font-medium">{formatCadenceTotal(cadenceTotal)}</span>
                </div>
              )}
              {oneTimeTotal > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("One-time subtotal")}</span>
                  <span className="font-medium">
                    {formatCurrency(oneTimeTotal, currency, { fromSmallestUnit: true })}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("Shipping")}</span>
                <span className="font-medium">{t("Calculated at checkout")}</span>
              </div>
            </div>
            <Separator />
            <div className="flex justify-between px-5 py-4">
              <span className="font-semibold text-foreground">{t("Total")}</span>
              <span className="font-bold text-foreground text-lg">{orderTotalLabel}</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">{t("Payment Method")}</h3>
          <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{t("Credit / Debit Card")}</p>
              <p className="text-xs text-muted-foreground">{t("Visa, Mastercard, Amex")}</p>
            </div>
          </div>

          <Button className="w-full mt-2" size="lg" loading={isPreparingCheckout} onClick={handlePlaceOrder}>
            {t("Place Order · {{total}}", { total: orderTotalLabel })}
          </Button>

          {/* checkoutError.message is backend or Stripe text, written for staff rather than endusers. */}
          {checkoutError && (
            <p className="text-sm text-destructive text-center">
              {checkoutError instanceof UnconfiguredShopProductError
                ? t("Something in your cart can't be purchased right now. Please contact your care team.")
                : t("We couldn't start checkout. Please try again or contact your care team.")}
            </p>
          )}

          <p className="text-xs text-muted-foreground text-center leading-relaxed px-2">
            {t("By placing your order you agree to our terms.")}
          </p>
        </div>
      </div>
    </div>
  );
};
